/**
 * `nodesMetadata.json` sidecar: client-only state that doesn't belong in the
 * workflow JSON definition (the runtime contract), but does need to survive
 * across devices and across page reloads.
 *
 * Why a sidecar instead of `metadata.*` fields in workflow.json?
 *   - Workflow JSON stays canonical: comparing definitions no longer flips
 *     on pixel deltas or client-only enhancement data.
 *   - The runtime ignores unknown metadata, but other tooling (validators,
 *     CI diffs, hand-edits) shouldn't have to know what to skip.
 *   - We can iterate on what "client state" means without re-saving every
 *     workflow definition.
 *
 * What lives here (today):
 *   - Pixel positions for triggers, actions, and agent tools
 *   - `swaggerPathTemplate` — derived OpenAPI path template attached at add
 *     time; never modified by the user; pure presentation/extraction concern
 *   - `detached` — graph-only flag for free-floating actions (no runAfter)
 *   - `layoutMode` — workflow-level auto/manual toggle for the canvas
 *
 * Mode awareness:
 *   - The file holds BOTH branches (`draft` + `published`). Same file is
 *     consulted regardless of which version the user is viewing.
 *   - Draft saves write to the `draft` branch only.
 *   - Publish snapshots `draft` into `published` BEFORE writing.
 *
 * Source of truth at runtime:
 *   - Positions: `workflowLayoutStore` (canvas reads from store, never from
 *     the in-memory action's `metadata.position`).
 *   - `swaggerPathTemplate`, `detached`, `layoutMode`: in-memory workflow
 *     tree. Reader sites are unchanged from before the sidecar existed.
 *     This sidecar is the *persistence* layer for these fields, not the
 *     consult-at-render layer.
 *
 * Round-trip:
 *   1. Load: fetch sidecar → `applySidecar` hydrates in-memory tree + store.
 *      Legacy values in `metadata.*` (from old workflows) are not overwritten
 *      when sidecar has nothing for that key; this keeps the migration
 *      forward-compatible (first save persists them into the sidecar).
 *   2. Save: `buildSidecar` reads in-memory tree + store → JSON.
 *      The workflow serializer strips the same fields from workflow.json so
 *      they never appear on disk in two places.
 */

import type { WorkflowDefinition, Action, Position } from '@/types/workflowDefinition';
import {
  useWorkflowLayoutStore,
  type WorkflowMode,
} from '@/stores/workflowLayoutStore';
import type { WorkflowIdentity } from '@/stores/workflowViewPrefsStore';

/** Current sidecar schema version. Bump when the on-disk shape changes. */
export const NODES_METADATA_VERSION = 1;

/** Per-node entry; all fields optional so absence is meaningful. */
export interface NodesMetadataNodeEntry {
  position?: Position;
  swaggerPathTemplate?: string;
  detached?: boolean;
}

export interface NodesMetadataBranch {
  layoutMode?: 'auto' | 'manual';
  /** Keyed by React Flow node id: `trigger-{name}` or `action-{scopedPath}`. */
  nodes?: Record<string, NodesMetadataNodeEntry>;
  /** Agent name → tool name → tool position (relative to parent agent). */
  toolPositions?: Record<string, Record<string, Position>>;
}

export interface NodesMetadataFile {
  version: typeof NODES_METADATA_VERSION;
  updatedAt: number;
  draft: NodesMetadataBranch;
  published: NodesMetadataBranch;
}

const EMPTY_BRANCH: NodesMetadataBranch = Object.freeze({});

export function emptyNodesMetadata(): NodesMetadataFile {
  return {
    version: NODES_METADATA_VERSION,
    updatedAt: 0,
    draft: {},
    published: {},
  };
}

function isPosition(value: unknown): value is Position {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as Position).x === 'number' &&
    typeof (value as Position).y === 'number' &&
    Number.isFinite((value as Position).x) &&
    Number.isFinite((value as Position).y)
  );
}

/**
 * Parse a raw sidecar value (e.g. a JSON.parse'd HTTP body) into a typed
 * file, dropping invalid entries. Returns `emptyNodesMetadata()` for any
 * unrecognized shape.
 */
export function parseNodesMetadata(raw: unknown): NodesMetadataFile {
  if (typeof raw !== 'object' || raw === null) return emptyNodesMetadata();
  const obj = raw as Record<string, unknown>;
  if (obj.version !== NODES_METADATA_VERSION) return emptyNodesMetadata();
  return {
    version: NODES_METADATA_VERSION,
    updatedAt: typeof obj.updatedAt === 'number' ? obj.updatedAt : 0,
    draft: sanitizeBranch(obj.draft),
    published: sanitizeBranch(obj.published),
  };
}

function sanitizeBranch(raw: unknown): NodesMetadataBranch {
  if (typeof raw !== 'object' || raw === null) return {};
  const obj = raw as Record<string, unknown>;
  const branch: NodesMetadataBranch = {};

  if (obj.layoutMode === 'auto' || obj.layoutMode === 'manual') {
    branch.layoutMode = obj.layoutMode;
  }

  if (typeof obj.nodes === 'object' && obj.nodes !== null) {
    const nodes: Record<string, NodesMetadataNodeEntry> = {};
    for (const [k, v] of Object.entries(obj.nodes as Record<string, unknown>)) {
      if (typeof k !== 'string' || k.length === 0 || typeof v !== 'object' || v === null) continue;
      const entry: NodesMetadataNodeEntry = {};
      const vv = v as Record<string, unknown>;
      if (isPosition(vv.position)) entry.position = { x: vv.position.x, y: vv.position.y };
      if (typeof vv.swaggerPathTemplate === 'string') entry.swaggerPathTemplate = vv.swaggerPathTemplate;
      if (typeof vv.detached === 'boolean') entry.detached = vv.detached;
      if (Object.keys(entry).length > 0) nodes[k] = entry;
    }
    if (Object.keys(nodes).length > 0) branch.nodes = nodes;
  }

  if (typeof obj.toolPositions === 'object' && obj.toolPositions !== null) {
    const toolPositions: Record<string, Record<string, Position>> = {};
    for (const [agent, tools] of Object.entries(obj.toolPositions as Record<string, unknown>)) {
      if (typeof agent !== 'string' || agent.length === 0 || typeof tools !== 'object' || tools === null) continue;
      const tt: Record<string, Position> = {};
      for (const [toolName, pos] of Object.entries(tools as Record<string, unknown>)) {
        if (typeof toolName === 'string' && toolName.length > 0 && isPosition(pos)) {
          tt[toolName] = { x: pos.x, y: pos.y };
        }
      }
      if (Object.keys(tt).length > 0) toolPositions[agent] = tt;
    }
    if (Object.keys(toolPositions).length > 0) branch.toolPositions = toolPositions;
  }

  return branch;
}

/**
 * Walk an action subtree and visit every action with its React Flow node id
 * (`action-{scopedPath}`). Returns the actions list as `(rfId, action,
 * parentScopedPath, actionName)` tuples — caller-friendly for both serialize
 * and apply.
 */
function* walkActions(
  actions: Record<string, Action> | undefined,
  scopedPath: string,
): Generator<{ rfId: string; action: Action; name: string; scopedPath: string }> {
  if (!actions) return;
  for (const [name, action] of Object.entries(actions)) {
    const fullPath = scopedPath ? `${scopedPath}/${name}` : name;
    yield { rfId: `action-${fullPath}`, action, name, scopedPath: fullPath };

    // Recurse into all known child action containers. `Action` is a union;
    // these fields exist on specific variants (If/Switch/Foreach/Until/Scope)
    // but the union itself doesn't expose them. Use a structural cast.
    const a = action as unknown as {
      actions?: Record<string, Action>;
      else?: { actions?: Record<string, Action> };
      cases?: Record<string, { actions?: Record<string, Action> }>;
      default?: { actions?: Record<string, Action> };
    };
    if (a.actions) yield* walkActions(a.actions, fullPath);
    if (a.else?.actions) yield* walkActions(a.else.actions, fullPath);
    if (a.cases) {
      for (const branch of Object.values(a.cases)) {
        if (branch?.actions) yield* walkActions(branch.actions, fullPath);
      }
    }
    if (a.default?.actions) yield* walkActions(a.default.actions, fullPath);
  }
}

/**
 * Build the sidecar file content for a single branch (typically `draft`)
 * from the in-memory workflow tree + layout store. Caller is responsible
 * for placing this into the right key of a {@link NodesMetadataFile}.
 */
export function buildBranch(
  workflow: WorkflowDefinition | null,
  identity: WorkflowIdentity,
  mode: WorkflowMode,
): NodesMetadataBranch {
  const branch: NodesMetadataBranch = {};
  const layoutState = useWorkflowLayoutStore.getState();
  const positions = layoutState.getNodePositions(identity, mode);
  const toolPositions = layoutState.getLayout(identity)?.[mode]?.toolNodePositions ?? {};

  // Workflow-level layout mode (auto/manual) lives at `workflow.metadata`
  // (the top-level metadata bag on WorkflowDefinition), not on the
  // definition itself. See `insertLayoutHelpers.getLayoutMode`.
  const wfMeta = workflow?.metadata as { layoutMode?: 'auto' | 'manual' } | undefined;
  if (wfMeta?.layoutMode === 'auto' || wfMeta?.layoutMode === 'manual') {
    branch.layoutMode = wfMeta.layoutMode;
  }

  const nodes: Record<string, NodesMetadataNodeEntry> = {};

  // Triggers
  const triggers = workflow?.definition.triggers ?? {};
  for (const name of Object.keys(triggers)) {
    const rfId = `trigger-${name}`;
    const pos = positions[rfId];
    const entry: NodesMetadataNodeEntry = {};
    if (pos) entry.position = pos;
    if (Object.keys(entry).length > 0) nodes[rfId] = entry;
  }

  // Actions (recursive walk)
  for (const { rfId, action } of walkActions(workflow?.definition.actions, '')) {
    const entry: NodesMetadataNodeEntry = {};
    const pos = positions[rfId];
    if (pos) entry.position = pos;
    const meta = action.metadata as { swaggerPathTemplate?: unknown } | undefined;
    if (typeof meta?.swaggerPathTemplate === 'string') {
      entry.swaggerPathTemplate = meta.swaggerPathTemplate;
    }
    if (action.detached === true) entry.detached = true;
    if (Object.keys(entry).length > 0) nodes[rfId] = entry;
  }

  if (Object.keys(nodes).length > 0) branch.nodes = nodes;

  // Tool positions are stored independent of the workflow tree (since tools
  // hang off agent actions and are layout-only); persist whatever's in the
  // store for the active branch.
  const cleanedToolPositions: Record<string, Record<string, Position>> = {};
  for (const [agent, tools] of Object.entries(toolPositions)) {
    if (Object.keys(tools).length > 0) cleanedToolPositions[agent] = { ...tools };
  }
  if (Object.keys(cleanedToolPositions).length > 0) {
    branch.toolPositions = cleanedToolPositions;
  }

  return branch;
}

/**
 * Build a full sidecar file. Writes the current in-memory state into the
 * `mode` branch and preserves the other branch from the {@link previous}
 * file (so a draft save never touches the published branch).
 */
export function buildSidecar(
  workflow: WorkflowDefinition | null,
  identity: WorkflowIdentity,
  mode: WorkflowMode,
  previous: NodesMetadataFile | null,
): NodesMetadataFile {
  const built = buildBranch(workflow, identity, mode);
  const prev = previous ?? emptyNodesMetadata();
  return {
    version: NODES_METADATA_VERSION,
    updatedAt: Date.now(),
    draft: mode === 'draft' ? built : prev.draft,
    published: mode === 'published' ? built : prev.published,
  };
}

/**
 * Result of applying a sidecar branch to an in-memory workflow.
 * The returned `workflow` is a shallow-cloned tree with sidecar values
 * injected (swaggerPathTemplate / detached / layoutMode). Caller passes
 * this to `setWorkflowFiles`. Position data has already been pushed into
 * the layout store as a side effect.
 */
export interface ApplySidecarResult {
  workflow: WorkflowDefinition | null;
}

/**
 * Apply a sidecar branch to the workflow tree + layout store. Sidecar
 * values override any matching `metadata.*` fields already present in
 * the tree (the sidecar is the new source of truth). When the sidecar
 * omits a key, the existing in-memory value is preserved — this allows
 * legacy workflows whose fields still live in `metadata.*` to migrate
 * over via the next save without losing data.
 */
export function applySidecarBranch(
  branch: NodesMetadataBranch | undefined,
  workflow: WorkflowDefinition | null,
  identity: WorkflowIdentity,
  mode: WorkflowMode,
): ApplySidecarResult {
  if (!workflow) return { workflow: null };
  const safeBranch = branch ?? EMPTY_BRANCH;

  // Hydrate positions into the layout store first. importLayout is a merge
  // (preserves any positions the user has dragged in this session that
  // aren't in the sidecar yet), which is the right semantics for a fresh
  // workflow load — the store starts empty per-workflow anyway.
  const nodePositions: Record<string, Position> = {};
  if (safeBranch.nodes) {
    for (const [rfId, entry] of Object.entries(safeBranch.nodes)) {
      if (entry.position) nodePositions[rfId] = entry.position;
    }
  }
  useWorkflowLayoutStore.getState().importLayout(identity, mode, {
    nodePositions,
    toolNodePositions: safeBranch.toolPositions ?? {},
  });

  // Rebuild the workflow tree with sidecar values injected.
  const nextDefinition = { ...workflow.definition };
  let nextMetadata = workflow.metadata as Record<string, unknown> | undefined;

  // Workflow-level layout mode (lives at `workflow.metadata.layoutMode`).
  if (safeBranch.layoutMode) {
    nextMetadata = {
      ...(nextMetadata ?? {}),
      layoutMode: safeBranch.layoutMode,
    };
  }

  // Per-action overrides
  if (safeBranch.nodes && nextDefinition.actions) {
    nextDefinition.actions = applyToActions(nextDefinition.actions, '', safeBranch.nodes);
  }

  const enrichedWorkflow: WorkflowDefinition = {
    ...workflow,
    definition: nextDefinition,
  };
  if (nextMetadata !== undefined) {
    (enrichedWorkflow as { metadata?: Record<string, unknown> }).metadata = nextMetadata;
  }

  return { workflow: enrichedWorkflow };
}

function applyToActions(
  actions: Record<string, Action>,
  scopedPath: string,
  sidecarNodes: Record<string, NodesMetadataNodeEntry>,
): Record<string, Action> {
  const out: Record<string, Action> = {};
  for (const [name, action] of Object.entries(actions)) {
    const fullPath = scopedPath ? `${scopedPath}/${name}` : name;
    const rfId = `action-${fullPath}`;
    const entry = sidecarNodes[rfId];

    let next: Action = action;
    if (entry?.swaggerPathTemplate !== undefined) {
      next = {
        ...next,
        metadata: { ...(next.metadata ?? {}), swaggerPathTemplate: entry.swaggerPathTemplate },
      };
    }
    if (entry?.detached !== undefined) {
      next = { ...next, detached: entry.detached };
    }

    // Recurse into child containers, replacing them with sidecar-applied
    // versions. As in `walkActions`, container fields aren't on the union;
    // cast structurally.
    const a = next as unknown as {
      actions?: Record<string, Action>;
      else?: { actions?: Record<string, Action> };
      cases?: Record<string, { actions?: Record<string, Action> }>;
      default?: { actions?: Record<string, Action> };
    };
    if (a.actions) {
      const newChildren = applyToActions(a.actions, fullPath, sidecarNodes);
      if (newChildren !== a.actions) {
        next = { ...(next as unknown as Record<string, unknown>), actions: newChildren } as unknown as Action;
      }
    }
    if (a.else?.actions) {
      const newChildren = applyToActions(a.else.actions, fullPath, sidecarNodes);
      if (newChildren !== a.else.actions) {
        next = {
          ...(next as unknown as Record<string, unknown>),
          else: { ...a.else, actions: newChildren },
        } as unknown as Action;
      }
    }
    if (a.cases) {
      const newCases: Record<string, { actions?: Record<string, Action> }> = {};
      let casesChanged = false;
      for (const [caseName, caseBranch] of Object.entries(a.cases)) {
        if (caseBranch?.actions) {
          const newChildren = applyToActions(caseBranch.actions, fullPath, sidecarNodes);
          if (newChildren !== caseBranch.actions) casesChanged = true;
          newCases[caseName] = { ...caseBranch, actions: newChildren };
        } else {
          newCases[caseName] = caseBranch;
        }
      }
      if (casesChanged) {
        next = { ...(next as unknown as Record<string, unknown>), cases: newCases } as unknown as Action;
      }
    }
    if (a.default?.actions) {
      const newChildren = applyToActions(a.default.actions, fullPath, sidecarNodes);
      if (newChildren !== a.default.actions) {
        next = {
          ...(next as unknown as Record<string, unknown>),
          default: { ...a.default, actions: newChildren },
        } as unknown as Action;
      }
    }

    out[name] = next;
  }
  return out;
}
